// @ts-nocheck
/**
 * Data Center list — redesigned to obd3-gui pattern (FillPage + PageHeader +
 * DataTable + DropdownMenu row actions + ConfirmDialog).
 * Preserved: hasPermission read guard, multi/single delete, getCountry
 * derived field, and v1 bug at list.js:533 (reads moduleOperations['Data
 * Center'] literal instead of MODULE_ACL.DATA_CENTRE) for the actions gate.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_DATA_CENTER_LIST, API_DATA_CENTER_DELETE } from '@/service/url';
import {
  ROUTES,
  DOMESTIC_COUNTRY,
  MODULE_ACL,
  USER_ROLES,
} from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const getCountry = (item: any) =>
  item?.type === 'International'
    ? item?.overseas_details?.country?.name
    : DOMESTIC_COUNTRY?.value;

const DataCenterList = () => {
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [dataCenter, setDataCenter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [moduleOperations, setModuleOperations] = useState<any>({});
  const [selectedData, setSelectedDataCenter] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(
        parseModuleOperations(user.acl_settings.module_operations),
      );
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchList();
    const user = getUser();
    if (user) {
      if (user.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
      if (user?.acl_settings) {
        setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperations(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_DATA_CENTER_LIST)
      .then((resp: any) => {
        const list = (resp || []).map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
          state:
            val.type === 'International'
              ? val.overseas_details?.state?.name
              : val.domestic_details?.state?.name,
          city:
            val.type === 'International'
              ? val.overseas_details?.city
              : val.domestic_details?.city,
        }));
        setDataCenter(list);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCheckboxToggle = (id: any) => {
    setSelectedDataCenter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedData.length === dataCenter.length) setSelectedDataCenter([]);
    else setSelectedDataCenter(dataCenter.map((dc: any) => dc.id));
  };

  const requestSingleDelete = (row: any) => {
    checkAuth(setToastProps, setShowToast);
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    const ids = pendingBulk ? selectedData : pendingDelete ? [pendingDelete.id] : [];
    if (!ids.length) return;
    setDeleting(true);
    try {
      await postApiCall(API_DATA_CENTER_DELETE, { dataCenterIds: ids });
      toast.success('Data Center deleted successfully');
      if (pendingBulk) setSelectedDataCenter([]);
      setPendingDelete(null);
      setPendingBulk(false);
      setLoading(true);
      fetchList();
    } catch (err: any) {
      if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err));
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'data-center-list',
        data: dataCenter,
        columns: [
          { header: 'Name', accessor: 'name' },
          { header: 'Type', accessor: 'type' },
          { header: 'Country', accessor: (row: any) => getCountry(row) || '-' },
          { header: 'State', accessor: 'state' },
          { header: 'City', accessor: 'city' },
          { header: 'Contact Person', accessor: 'contact_person' },
          { header: 'Contact Email', accessor: 'contact_email' },
          { header: 'Contact Number', accessor: 'contact_number' },
          { header: 'Data Centre Company', accessor: 'data_centre_company' },
          { header: 'Data Centre Address', accessor: 'data_centre_address' },
          { header: 'Created Date', accessor: (row: any) => formatDate(row.created_at) },
          { header: 'Updated Date', accessor: (row: any) => formatDate(row.updated_at) },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.DATA_CENTRE,
    'read',
  );
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const canAdd = isSuperAdmin;
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={selectedData.length === dataCenter.length && dataCenter.length > 0}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedData.includes(row.original.id)}
            onCheckedChange={() => handleCheckboxToggle(row.original.id)}
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.DATA_CENTER.EDIT.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.name}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{row.original.name}</span>
          ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 120,
        cell: ({ row }) => (
          <span
            className={
              row.original.type === 'International'
                ? 'inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25'
                : 'inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60'
            }
          >
            {row.original.type}
          </span>
        ),
      },
      {
        accessorFn: (row: any) => getCountry(row),
        id: 'country',
        header: 'Country',
      },
      { accessorKey: 'state', header: 'State' },
      { accessorKey: 'city', header: 'City' },
      { accessorKey: 'contact_person', header: 'Contact Person' },
      {
        accessorKey: 'contact_email',
        header: 'Email',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.contact_email || '—'}</span>
        ),
      },
      {
        accessorKey: 'contact_number',
        header: 'Phone',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.contact_number || '—'}</span>
        ),
      },
      { accessorKey: 'data_centre_company', header: 'Company' },
      { accessorKey: 'data_centre_address', header: 'Address' },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/${ROUTES.DATA_CENTER.EDIT.replace(':id', row.original.id)}`);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {selectedData.length === 0 && canDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestSingleDelete(row.original);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                  title="Delete"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
          </div>
        ),
      });
    }
    return cols;
  }, [
    selectedData,
    dataCenter,
    canEdit,
    showActions,
    isSuperAdmin,
    moduleOperations,
    navigate,
  ]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Data Centers" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Data Centers"
        description="Onboard the data-centres where PBX / telephony infra runs."
        actions={
          <>
            {canDelete && selectedData.length > 0 && (
              <Button variant="destructive" onClick={() => setPendingBulk(true)}>
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedData.length})
              </Button>
            )}
            {dataCenter.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.DATA_CENTER.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Data Center
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="data-center.list:v1"
        columns={columns}
        data={dataCenter}
        isLoading={loading}
        searchPlaceholder="Search by name…"
        emptyTitle="No data centers"
        emptyDescription="No data centers match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected data centers?' : 'Delete this data center?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">{selectedData.length}</span>{' '}
              data center(s) will be permanently deleted.
            </>
          ) : (
            <>
              Data center{' '}
              <span className="font-medium text-foreground">{pendingDelete?.name}</span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default DataCenterList;
